import {
  type Action,
  ActionCategory,
  TransferFhsaToRrspAction_Reason,
} from "../../generated/core/v1/actions";
import type { Date as PDate } from "../../generated/core/v1/date";
import { addMonths, compareDates, monthsBetween } from "../lib/dates";
import { type Plan, startDate } from "./core";

/**
 * Actions are the money movements the plan makes. A flat view of each, so pages don't switch on
 * the oneof: how much, where from or to, and whether money leaves chequing or comes back to it.
 */
export type FlatAction = {
  action: Action;
  date: PDate;
  amount: number;
  /** The entity the money goes to (or comes from, for `direction: "in"`). */
  targetUuid: string;
  /** "out": chequing pays something. "in": money comes back to chequing. "move": between accounts. */
  direction: "out" | "in" | "move" | "none";
  kind: NonNullable<Action["actionType"]["oneofKind"]> | "createdAccountMove";
  category: ActionCategory;
  mandatory: boolean;
  memberUuid: string;
};

/** Flattens one action. CreateAccount actions expand into their dated moves. */
export function flattenAction(a: Action): FlatAction[] {
  const base = {
    action: a,
    date: a.date!,
    category: a.category,
    mandatory: a.mandatory,
    memberUuid: a.memberUuid,
  };
  const t = a.actionType;
  switch (t.oneofKind) {
    case "moveMoneyFromChequingToAccountAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: t.moveMoneyFromChequingToAccountAction.amount,
          targetUuid: t.moveMoneyFromChequingToAccountAction.destinationAccountUuid,
          direction: "out",
        },
      ];
    case "moveMoneyFromChequingToDebtAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: t.moveMoneyFromChequingToDebtAction.amount,
          targetUuid: t.moveMoneyFromChequingToDebtAction.destinationDebtUuid,
          direction: "out",
        },
      ];
    case "moveMoneyFromChequingToExpenseAction": {
      const e = t.moveMoneyFromChequingToExpenseAction;
      const target =
        e.destinationExpense.oneofKind === "destinationExpenseUuid"
          ? e.destinationExpense.destinationExpenseUuid
          : e.associatedMortgageUuid;
      return [
        { ...base, kind: t.oneofKind, amount: e.amount, targetUuid: target, direction: "out" },
      ];
    }
    case "moveMoneyIntoChequingAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: t.moveMoneyIntoChequingAction.amount,
          targetUuid: t.moveMoneyIntoChequingAction.sourceAccountUuid,
          direction: "in",
        },
      ];
    case "payPriorityAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: t.payPriorityAction.amount,
          targetUuid: t.payPriorityAction.priorityUuid,
          direction: "out",
        },
      ];
    case "transferFhsaToRrspAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: t.transferFhsaToRrspAction.amount,
          targetUuid: t.transferFhsaToRrspAction.destinationRrspUuid,
          direction: "move",
        },
      ];
    case "convertRrspToRrifAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: t.convertRrspToRrifAction.amount,
          targetUuid: t.convertRrspToRrifAction.rrspUuid,
          direction: "move",
        },
      ];
    case "createHouseAndMortgageAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: t.createHouseAndMortgageAction.downPayment,
          targetUuid: t.createHouseAndMortgageAction.priorityUuid,
          direction: "out",
        },
      ];
    case "reamortizeDebtsIntoMortgageAction": {
      const r = t.reamortizeDebtsIntoMortgageAction;
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: r.debtTransfers.reduce((s, d) => s + d.amount, 0),
          targetUuid: r.mortgageUuid,
          direction: "move",
        },
      ];
    }
    case "setGroupRrspContributionAction":
      return [
        {
          ...base,
          kind: t.oneofKind,
          amount: 0, // a rate, not a movement
          targetUuid: t.setGroupRrspContributionAction.jobUuid,
          direction: "none",
        },
      ];
    case "createAccountAction": {
      const c = t.createAccountAction;
      const target = c.createdAccountUuid;
      const ins = c.moveMoneyFromChequingToCreatedAccountActions.map(
        (m): FlatAction => ({
          ...base,
          date: m.date ?? a.date!,
          kind: "createdAccountMove",
          amount: m.amount,
          targetUuid: target,
          direction: "out",
          category: a.category || ActionCategory.SAVING,
        }),
      );
      const outs = c.moveMoneyFromCreatedAccountToChequingActions.map(
        (m): FlatAction => ({
          ...base,
          date: m.date ?? a.date!,
          kind: "createdAccountMove",
          amount: m.amount,
          targetUuid: target,
          direction: "in",
          category: ActionCategory.WITHDRAWAL,
        }),
      );
      return [...ins, ...outs];
    }
    default:
      return [];
  }
}

/** Every action of the plan, flattened and in date order. */
export function flatActions(plan: Plan): FlatAction[] {
  if (!plan) return [];
  return plan.actions
    .flatMap(flattenAction)
    .filter((f) => !!f.date)
    .sort((a, b) => compareDates(a.date, b.date) || a.action.id - b.action.id);
}

export function actionsBetween(
  plan: Plan,
  from: PDate | undefined,
  to: PDate | undefined,
  all: FlatAction[] = flatActions(plan),
): FlatAction[] {
  return all.filter(
    (f) => (!from || compareDates(f.date, from) >= 0) && (!to || compareDates(f.date, to) < 0),
  );
}

/** Sum of amounts by category over [from, to), outflows only ("out"). */
export function categoryTotals(actions: FlatAction[]): Map<ActionCategory, number> {
  const out = new Map<ActionCategory, number>();
  actions
    .filter((f) => f.direction === "out")
    .forEach((f) => out.set(f.category, (out.get(f.category) ?? 0) + f.amount));
  return out;
}

/**
 * The plan's recommendations from its start date: non-mandatory actions, in date order. The same
 * action repeating (same kind and target) is listed once, at its first date, with how often it
 * recurs in the window.
 */
export type Recommendation = {
  first: FlatAction;
  occurrences: number;
  total: number;
};

export function upcomingRecommendations(
  plan: Plan,
  opts: { months?: number; limit?: number; from?: PDate } = {},
): Recommendation[] {
  const from = opts.from ?? startDate(plan);
  if (!from) return [];
  const to = addMonths(from, opts.months ?? 3);
  const recs = new Map<string, Recommendation>();
  actionsBetween(plan, from, to)
    .filter((f) => !f.mandatory && (f.amount > 0 || f.kind === "setGroupRrspContributionAction"))
    .forEach((f) => {
      const key = `${f.kind}:${f.targetUuid}:${f.direction}`;
      const r = recs.get(key);
      if (r) {
        r.occurrences += 1;
        r.total += f.amount;
      } else recs.set(key, { first: f, occurrences: 1, total: f.amount });
    });
  // a contribution rate has no amount but lasts all year, so it leads its date
  const contribution = (r: Recommendation) =>
    Number(r.first.kind === "setGroupRrspContributionAction");
  const list = [...recs.values()].sort(
    (a, b) =>
      compareDates(a.first.date, b.first.date) ||
      contribution(b) - contribution(a) ||
      b.total - a.total,
  );
  return opts.limit ? list.slice(0, opts.limit) : list;
}

/**
 * The check-in list: the plan's recommended actions between `meta.simulatedFrom` and `today`
 * (what the plan predicts the household did since its facts were recorded).
 */
export function checkInActions(plan: Plan, today: PDate | undefined): Action[] {
  if (!plan || !today) return [];
  const from = plan.meta?.simulatedFrom;
  if (!from) return [];
  return plan.actions
    .filter(
      (a) =>
        !a.mandatory &&
        a.date &&
        compareDates(a.date, from) >= 0 &&
        compareDates(a.date, today) < 0 &&
        a.actionType.oneofKind !== undefined,
    )
    .sort((a, b) => compareDates(a.date, b.date) || a.id - b.id);
}

/**
 * FHSA moves to the RRSP the plan makes because the data says the holder isn't a first-time home
 * buyer, which the household hasn't confirmed (the data may be wrong): every date from
 * `meta.simulatedFrom`, so a move due before today (a check-in's) is included.
 */
export function unconfirmedFhsaTransfers(plan: Plan): FlatAction[] {
  if (!plan) return [];
  return plan.actions
    .filter(isUnconfirmedFhsaTransfer)
    .flatMap(flattenAction)
    .filter((f) => f.amount > 0);
}

/** Whether an action is a move from `unconfirmedFhsaTransfers`. */
export function isUnconfirmedFhsaTransfer(a: Action): boolean {
  return (
    a.actionType.oneofKind === "transferFhsaToRrspAction" &&
    a.actionType.transferFhsaToRrspAction.reason ===
      TransferFhsaToRrspAction_Reason.NOT_FIRST_TIME_BUYER_UNCONFIRMED
  );
}

/** Months covered by [from, to), at least 1. */
export function monthsIn(from: PDate, to: PDate): number {
  return Math.max(1, monthsBetween(from, to));
}

/** "Where it should go next": per month, the plan's non-mandatory outflows grouped by target. */
export type NextMonth = {
  month: PDate;
  total: number;
  items: {
    targetUuid: string;
    amount: number;
    category: ActionCategory;
    kind: FlatAction["kind"];
  }[];
};

export function whereNext(plan: Plan, months = 6): NextMonth[] {
  const from = startDate(plan);
  if (!from) return [];
  const all = flatActions(plan);
  const out: NextMonth[] = [];
  for (let i = 0; i < months; i++) {
    const a = i === 0 ? from : { ...addMonths(from, i), day: 1 };
    const b = { ...addMonths(from, i + 1), day: 1 };
    const items = new Map<string, NextMonth["items"][number]>();
    actionsBetween(plan, a, b, all)
      .filter((f) => !f.mandatory && f.direction === "out" && f.amount > 0)
      .forEach((f) => {
        const it = items.get(f.targetUuid);
        if (it) it.amount += f.amount;
        else
          items.set(f.targetUuid, {
            targetUuid: f.targetUuid,
            amount: f.amount,
            category: f.category,
            kind: f.kind,
          });
      });
    const list = [...items.values()].sort((x, y) => y.amount - x.amount);
    out.push({ month: a, total: list.reduce((s, x) => s + x.amount, 0), items: list });
  }
  return out;
}
